// The page shell every city runs, wired from its parts: the map and the
// train canvas (map-frame.ts), the network and station layers
// (network-view.ts), the line list and its summary (roster.ts), picking,
// hover labels, cards and Follow (selection.ts), the clock (clock.ts),
// "Report a glitch" (glitch-report.ts), "Show where I am" (locate.ts), the
// toast, the loader, and the touch handling that keeps the page itself from
// zooming (touch-guard.ts).
// A city gives it its lines, its view and words, how a train's run is
// listed, and feeds it a network and trains; London also plugs in its
// National Rail layer through ShellExtras.

import { setWorkerUrl } from 'maplibre-gl';
import type { LngLatBoundsLike, Map as MapLibreMap } from 'maplibre-gl';
import * as Core from '@tol/core/core';
import * as Feed from '@tol/core/feed';
import type { ApiClient } from '@tol/core/feed';
import type { Drawn } from '@tol/core/glitch';
import { addNetworkLayers } from '@tol/core/layers';
import { countTrains, viewBox } from '@tol/core/onscreen';
import type { Box } from '@tol/core/onscreen';
import type { Renderer } from '@tol/core/render';
import { ZOOM_OFFSET } from '@tol/core/view';
import type { MapView } from '@tol/core/view';
import type {
  GeometryFile,
  LatLon,
  LineId,
  Network,
  PageLine,
  ScreenPoint,
  StationId,
  TrainStore,
} from '@tol/core/types';
import type { CardHooks } from './card.ts';
import { createClock } from './clock.ts';
import { el } from './dom.ts';
import { createGlitchReport } from './glitch-report.ts';
import { addLocateControl } from './locate.ts';
import { createMapFrame, textScale } from './map-frame.ts';
import { createNetworkView } from './network-view.ts';
import { createRoster, rosterGroups } from './roster.ts';
import type { ExtraRow, LineGroup, Noun, RosterScope, RosterStatus } from './roster.ts';
import { createSelection } from './selection.ts';
import type { ExtraPick, StationHit } from './selection.ts';
import type { Selection, ShellState } from './state.ts';
import { guardTouch } from './touch-guard.ts';

export type { Selection, ShellState } from './state.ts';
export type { ExtraPick, StationHit } from './selection.ts';
export {
  accuracyHtml,
  countText,
  foldedRows,
  groupNoun,
  groupRows,
  rosterGroups,
  statusDetailHtml,
  summaryHtml,
} from './roster.ts';
export type {
  LineGroup,
  Noun,
  Placement,
  RosterGroup,
  RosterKeep,
  RosterScope,
  RosterStatus,
  StatusDetail,
  StatusKind,
} from './roster.ts';

// Where a city plugs its own drawing, picking, rows and cards in.
export interface ShellExtras {
  // After the network layers exist.
  readonly onMapReady?: (shell: Shell) => void;
  // Drawn on the train canvas after the shell's trains, each frame.
  readonly draw?: (ctx: CanvasRenderingContext2D, dt: number, scale: number, width: number, height: number) => void;
  // Its trains on screen, for the glitch watcher.
  readonly drawn?: () => readonly Drawn[];
  readonly pick?: (pt: ScreenPoint, radius: number) => ExtraPick | null;
  readonly stationAt?: (pt: ScreenPoint, radius: number) => StationHit | null;
  // Renders a selection of its own type; false for one it doesn't know.
  readonly renderCard?: (card: HTMLElement, selection: Selection, full: boolean) => boolean;
  // Rows after the city's lines in the list, with a fixed status text.
  readonly rosterRows?: () => readonly ExtraRow[];
  // Train counts for those rows (null: no data yet), also counted in the
  // summary; only those in the box when given one (the map's view).
  readonly counts?: (within?: Box) => ReadonlyMap<string, number> | null;
  // The set of shown stations changed.
  readonly onStationsVisible?: (visible: ReadonlySet<StationId>) => void;
  // Where a followed selection of its own type is, for Follow.
  readonly followed?: (selection: Selection) => LatLon | null;
}

export interface ShellOptions {
  // The window property the console and benches use (tubeLive, nycLive).
  readonly hooks: string;
  // Sent with glitch reports.
  readonly city: string;
  readonly lines: readonly PageLine[];
  readonly groups?: readonly LineGroup[];
  readonly accuracyGroups?: readonly LineGroup[];
  readonly accuracyLabel?: (name: string) => string;
  readonly view: {
    readonly bounds: { readonly phone: LngLatBoundsLike; readonly desktop: LngLatBoundsLike };
    readonly minZoom: number;
    readonly maxZoom: number;
    readonly maxBounds: LngLatBoundsLike;
    // Phones open on bounds.phone as fitted, not at the city zoom below
    // (a country's network: Amtrak's). Default: no.
    readonly phoneFits?: boolean;
  };
  // Attribution besides MapLibre's and the basemap's.
  readonly credits: readonly string[];
  // Trains ride the right-hand track once lines split (American railways);
  // per line on a map of several countries.
  readonly keepRight?: boolean | ((lineId: LineId) => boolean);
  readonly timeZone: string;
  readonly locale: string;
  // What the summary calls what runs: "12 trains running on 3 lines" (the
  // default), "12 vehicles running on 3 lines".
  readonly noun?: Noun;
  // "TfL's newest predictions", for the updated line's hover.
  readonly dataName: string;
  // Data older than this (s) turns the live dot stale.
  readonly staleAfterS?: number | ((group: number) => number);
  // The first trains are on their way (a server's first refresh after a
  // quiet spell): the summary says so while none are in, not "0 trains".
  readonly loading?: () => boolean;
  readonly card: CardHooks;
  readonly status?: (id: LineId) => RosterStatus | undefined;
  // Why a line has no trains ("closed", "not running now", "no live data"),
  // in place of "0 trains"; undefined: "0 trains".
  readonly idle?: (id: LineId) => string | undefined;
  // The page went hidden (stop polling) or came back (poll now).
  readonly onHidden?: () => void;
  readonly onVisible?: () => void;
  // "Show where I am" is about to move the camera to the viewer.
  readonly onLocate?: () => void;
  // Current detailed geometry was submitted; usable tiles can arrive later.
  readonly onLinesSubmitted?: (net: Network, bounds: Box) => void;
  readonly extras?: ShellExtras;
  // The API client to share (its clock offset), when the city made one first.
  readonly client?: ApiClient;
  // More for the window hooks object.
  readonly extraHooks?: Readonly<Record<string, unknown>>;
  // The list's first rows (setRosterScope): the world map's none, until a
  // city is in view. Default: every line.
  readonly rosterScope?: RosterScope;
}

export interface Shell {
  readonly state: ShellState;
  readonly live: TrainStore;
  readonly client: ApiClient;
  readonly map: MapLibreMap;
  readonly view: MapView;
  readonly renderer: Renderer;
  readonly lineStyle: (id: LineId) => PageLine;
  readonly hasLine: (id: LineId) => boolean;
  readonly now: () => number;
  readonly fmtEta: (ms: number) => string;
  readonly clockTime: (ms: number) => string;
  // The clock's and cards' time zone from now on (the world map's follows
  // the city in view), and a label over the clock ("Sydney").
  readonly setTimeZone: (timeZone: string, locale: string, label?: string) => void;
  readonly stationName: (sid: StationId) => string;
  // Replaces the active network and redraws. An optional retained superset
  // keeps station sources and layouts for revisits; detail is active-only.
  readonly setNetwork: (net: Network, rendered?: Network) => void;
  // After the store changed: counts, the list, the updated line.
  readonly updateArrivalUI: () => void;
  readonly draw: (dt?: number) => void;
  readonly renderRoster: () => void;
  // The list has rows for these lines only, from now on.
  readonly setRosterScope: (scope: RosterScope) => void;
  // More lines and their groups, before any network with them: the world
  // map's, as each city's code comes in.
  readonly addLines: (
    lines: readonly PageLine[],
    groups: readonly LineGroup[],
    accuracyGroups?: readonly LineGroup[],
  ) => void;
  readonly renderCard: (full: boolean) => void;
  readonly select: (selection: Selection) => void;
  readonly closeCard: () => void;
  readonly setFollowing: (on: boolean) => void;
  readonly showToast: (html: string) => void;
  readonly hideToast: () => void;
  readonly setLoader: (text: string, fraction?: number) => void;
  // The first network and trains are in: hide the loader, start drawing.
  readonly ready: () => void;
  // Whether the page is drawing (after ready).
  readonly running: () => boolean;
  // The followed train's position, if following.
  readonly followed: () => LatLon | null;
  // Resets the motion model (after the page was hidden).
  readonly resetMotion: () => void;
  // Whether the network layers' basemap is drawn.
  readonly hasBasemap: boolean;
  readonly reduceMotion: boolean;
  readonly version: string;
  readonly phone: boolean;
  readonly params: URLSearchParams;
  readonly isGeometryFile: (v: unknown) => v is GeometryFile;
}

declare global {
  interface Window {
    [hooks: string]: unknown;
  }
}

function hideToast(): void {
  el('#toast').hidden = true;
}

function setLoader(text: string, fraction?: number): void {
  el('#loader-text').textContent = text;
  if (fraction !== undefined) el('#loader-bar').style.width = `${fraction * 100}%`;
}

export async function createShell(options: ShellOptions): Promise<Shell> {
  const { extras = {}, staleAfterS = 180 } = options;
  // The renderer, the network view and the list read these as they are:
  // addLines grows them.
  const LINES: PageLine[] = [...options.lines];
  const LINE: Partial<Record<LineId, PageLine>> = Object.fromEntries(LINES.map((l) => [l.id, l]));
  const lineStyle = (id: LineId): PageLine => {
    const l = LINE[id];
    if (!l) throw new TypeError(`no style for line ${id}`);
    return l;
  };
  const hasLine = (id: LineId): boolean => LINE[id] !== undefined;

  const version = new URL(import.meta.url).search;
  setWorkerUrl(new URL(`./worker.js${version}`, import.meta.url).href);
  const params = new URLSearchParams(location.search);
  guardTouch();

  const state: ShellState = {
    net: null,
    layouts: new Map(),
    visibleStations: new Set(),
    hidden: new Set(),
    focus: null,
    selected: null,
    follow: false,
    lastUpdate: 0,
    counts: {},
    scheduled: {},
    gps: {},
    inView: null,
  };
  const live = Feed.createStore();
  // Relative, so the page works wherever it's mounted.
  const client = options.client ?? Feed.createClient({ base: '' });
  const now = (): number => Date.now() + client.offset;
  function stationName(id: StationId): string {
    return state.net?.stations.get(id)?.name ?? 'Unknown';
  }

  // The parts call each other only once the page is running (a frame, an
  // event), after every one of them exists.
  const frame = await createMapFrame(options, {
    lines: LINE,
    debug: params.has('debug'),
    zoom: params.get('z'),
    state,
    live,
    now,
    drawExtra: extras.draw,
    extraMoving: () => (extras.drawn?.().length ?? 0) > 0,
    onMotion: () => {
      glitches.watch();
    },
    onFrame: (ts) => {
      selection.follow(ts);
    },
    onRemoved: (removed) => {
      selection.trainsRemoved(removed);
    },
    followed: () => selection.followed(),
  });
  const { map, view, renderer, draw } = frame;

  const clock = createClock({
    timeZone: options.timeZone,
    locale: options.locale,
    now,
    live,
    lastUpdate: () => state.lastUpdate,
    staleAfterS,
    dataName: options.dataName,
  });

  const selection = createSelection({
    map,
    view,
    renderer,
    state,
    live,
    lineStyle,
    hasLine,
    now,
    clock,
    stationName,
    card: options.card,
    extension: extras,
  });
  const { select, closeCard, renderCard } = selection;

  const network = createNetworkView({
    map,
    lines: LINES,
    line: LINE,
    state,
    onStationsVisible: extras.onStationsVisible,
    ...(options.onLinesSubmitted ? { onLinesSubmitted: options.onLinesSubmitted } : {}),
    redraw: () => {
      draw(0);
    },
  });
  function setNetwork(net: Network, rendered?: Network): void {
    network.setNetwork(net, rendered);
    selection.hideHover();
    map.getCanvas().style.cursor = '';
    if (state.selected?.type === 'station' && !net.stations.has(state.selected.id)) closeCard();
    roster.render();
    if (state.selected) renderCard(true);
    draw(0);
  }

  let toastTimer: ReturnType<typeof setTimeout> | undefined;
  function showToast(html: string): void {
    const t = el('#toast');
    t.innerHTML = html;
    t.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 15000);
  }

  const glitches = createGlitchReport({
    map,
    live,
    state,
    city: options.city,
    version,
    drawn: extras.drawn,
    showToast,
  });

  addLocateControl(map, {
    say: showToast,
    beforeCenter: () => {
      options.onLocate?.();
      if (!state.follow) return;
      selection.setFollowing(false);
      renderCard(false);
    },
  });

  // The trains, all and in view: when they arrive and when the camera stops,
  // not per frame (a pass over every train).
  let inViewBox: Box | null = null;
  function countAll(): void {
    const b = map.getBounds();
    inViewBox = viewBox([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    const { all, inView } = countTrains(live.trains, state.net, inViewBox);
    state.counts = all.counts;
    state.scheduled = all.scheduled;
    state.gps = all.gps;
    state.inView = inView;
  }

  const roster = createRoster({
    lines: LINES,
    line: LINE,
    groups: rosterGroups(LINES, options.groups),
    accuracyGroups: options.accuracyGroups,
    accuracyLabel: options.accuracyLabel,
    lineStyle,
    state,
    missing: network.missing,
    inViewBox: () => inViewBox,
    extension: { rows: extras.rosterRows, counts: extras.counts },
    noun: options.noun,
    loading: options.loading,
    status: options.status,
    idle: options.idle,
    scope: options.rosterScope,
    setLinesVisible: (ids, visible) => {
      network.setLinesVisible(ids, visible);
      if (state.follow && selection.followed() === null) {
        selection.setFollowing(false);
        renderCard(false);
      }
      roster.render();
      draw(0);
    },
    setFocus: network.setFocus,
  });

  function addLines(
    more: readonly PageLine[],
    groups: readonly LineGroup[],
    accuracyGroups?: readonly LineGroup[],
  ): void {
    for (const l of more) {
      if (LINE[l.id]) continue;
      LINES.push(l);
      LINE[l.id] = l;
    }
    roster.addGroups(rosterGroups(more, groups), accuracyGroups);
  }

  function updateArrivalUI(): void {
    state.lastUpdate = live.groupUpdates.size > 0 ? Math.min(...live.groupUpdates.values()) : 0;
    countAll();
    roster.render();
    // Wake a frame loop that correctly went idle while no vehicles existed.
    draw(0);
  }
  map.on('moveend', () => {
    countAll();
    roster.render(true);
  });

  setInterval(() => {
    clock.tick();
    if (state.selected) renderCard(false);
  }, 1000);

  function ready(): void {
    if (frame.running()) return;
    el('#loader').classList.add('done');
    frame.start();
  }

  const shell: Shell = {
    state,
    live,
    client,
    map,
    view,
    renderer,
    lineStyle,
    hasLine,
    now,
    fmtEta: clock.fmtEta,
    clockTime: clock.clockTime,
    setTimeZone: clock.setTimeZone,
    stationName,
    setNetwork,
    updateArrivalUI,
    draw,
    renderRoster: () => {
      roster.render();
    },
    setRosterScope: roster.setScope,
    addLines,
    renderCard,
    select,
    closeCard,
    setFollowing: selection.setFollowing,
    showToast,
    hideToast,
    setLoader,
    ready,
    running: frame.running,
    followed: selection.followed,
    resetMotion: frame.resetMotion,
    hasBasemap: frame.hasBasemap,
    get reduceMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },
    version,
    get phone() {
      return window.matchMedia('(max-width: 680px)').matches;
    },
    params,
    isGeometryFile: Core.isGeometryFile,
  };
  // Handy for poking around in the console, and what the benches drive.
  window[options.hooks] = { state, live, map, view, zoomOffset: ZOOM_OFFSET, ...options.extraHooks };

  await new Promise<void>((resolve) => {
    map.once('load', () => {
      addNetworkLayers(map, frame.hasBasemap, textScale(), window.matchMedia('(max-width: 680px)').matches);
      extras.onMapReady?.(shell);
      frame.install();
      resolve();
    });
  });
  return shell;
}
